/**
 * Identifier capture (ENG-9699) — ONE definition, three consumers.
 *
 * THE DEFECT. An agent handed a URL in chat wrote a *summary* to memory ("a
 * Drive link is coming") and never the identifier itself. Conversation history
 * does not survive a restart; the summary does. So the agent reliably preserved
 * the re-derivable half and discarded the only irreplaceable half, and the
 * failure stayed silent until a restart landed between "received" and "used" —
 * at which point the user experiences it as forgetfulness rather than as a
 * capture bug. Re-deriving a summary is free; re-deriving a URL is impossible
 * without going back to the human.
 *
 * THE AGENT WAS FOLLOWING INSTRUCTIONS, which is why this is a class and not an
 * incident. Two layers told it to discard the payload:
 *   - the generated CLAUDE.md ("Don't save ... ephemeral task details"), and
 *   - the host extraction prompt ("not a one-off task detail or pleasantry").
 * A Drive folder link handed over mid-conversation reads, on a plain reading,
 * as exactly an ephemeral one-off task detail.
 *
 * WHY ONE MODULE. The rule is stated twice in prose (to the agent, to the
 * extraction model) and once as a regex (the deterministic detector). Three
 * definitions of "an identifier worth keeping" in three files is precisely how
 * they drift, and the drift is invisible — each layer keeps working while
 * quietly disagreeing about what it protects. They co-ship: `apps/cli` bundles
 * `@augmented/core` inline (`noExternal`, apps/cli/tsup.config.ts), so both
 * consumers are one compiled artifact published atomically. There is no
 * mixed-version fleet argument for duplicating the text here, and no deploy
 * cost to sharing it.
 *
 * NO `node:crypto` IN THIS MODULE. `packages/core/src/index.ts` is imported by
 * the webapp, so anything reachable from the barrel must stay browser-safe.
 * `identifierMemoryName` therefore uses a pure-JS FNV-1a hash: it is a name
 * discriminator, never a security boundary.
 */

/**
 * URL matcher. Deliberately permissive on the tail and trimmed afterwards —
 * a URL at the end of a sentence picks up the full stop, and a URL in prose
 * picks up the closing bracket, neither of which belongs to the identifier.
 */
const URL_RE = /\bhttps?:\/\/[^\s<>"'`]+/gi;

/** Trailing characters that are punctuation around a URL, never part of it. */
const TRAILING_PUNCT = new Set(['.', ',', ';', ':', '!', '?', ')', ']', '}', '>', '"', "'", '\u2019', '\u201d']);

/**
 * Trim sentence punctuation from a matched URL, but keep balanced brackets that
 * are genuinely part of it (Sharepoint and Confluence both emit those).
 */
export function trimUrlPunctuation(raw: string): string {
  // ONE character per iteration, and the balance check runs before each `)` is
  // dropped. Stripping the whole trailing run at once (`[...]+$`) looked
  // equivalent and was not: on `…/wiki/Foo_(bar).` the class consumed `)` and
  // `.` together, so the balance check never saw a string ending in `)` and the
  // URL lost the paren that belonged to it (CodeRabbit, verified).
  let out = raw;
  for (;;) {
    const last = out.at(-1);
    if (last === undefined || !TRAILING_PUNCT.has(last)) return out;
    // Balanced: the URL opened this group, so the paren is part of it
    // (Wikipedia, SharePoint and Confluence all emit these). Only an unmatched
    // ')' is prose punctuation.
    if (last === ')' && countOf(out, '(') >= countOf(out, ')')) return out;
    out = out.slice(0, -1);
  }
}

function countOf(s: string, ch: string): number {
  let n = 0;
  for (const c of s) if (c === ch) n += 1;
  return n;
}

/** Every distinct URL in `text`, in first-seen order, punctuation-trimmed. */
export function findIdentifierUrls(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of text.matchAll(URL_RE)) {
    const url = trimUrlPunctuation(m[0]);
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}

/**
 * Query/fragment parameter NAMES whose value is a credential rather than an
 * identifier. Matched as a substring so `access_token`, `x-api-key` and
 * `signature` are all caught.
 *
 * `resourcekey` is deliberately ABSENT. Google emits it on restricted-share
 * Drive links and the link 404s or permission-errors without it — redacting it
 * would hand back a URL that parses fine and does not work, which reads as "the
 * link rotted" and misdirects whoever debugs it. It is a capability value, so
 * this is a real trade-off, made on the side of the identifier being usable.
 */
const SECRET_PARAM_TOKENS = new Set([
  'token',
  'password',
  'passwd',
  'pwd',
  'secret',
  'auth',
  'bearer',
  'signature',
  'sig',
  'credential',
  'credentials',
  'session',
  'jwt',
  'apikey',
  // An OAuth authorization code and a one-time passcode are credentials, not
  // identifiers (CodeRabbit). `code` does have benign uses (`?code=US`,
  // `?code=PROMO10`); redacting those costs a query value, whereas persisting a
  // real authorization code into a durable, searchable store is a credential in
  // the memory table. Redaction is the cheaper error.
  'code',
  'otp',
]);

/** Concatenated spellings that carry no separator to split on. */
const SECRET_PARAM_SUBSTRINGS = ['apikey', 'accesstoken', 'authtoken', 'sessiontoken'];

/**
 * Replacement for a credential-bearing parameter VALUE. Deliberately bracket-
 * free: `URL.toString()` percent-encodes `[` and `]`, so `[redacted]` comes back
 * out as `%5Bredacted%5D` — correct, but noise in a string a human reads.
 */
const REDACTED_PARAM_VALUE = 'redacted';

function isSecretParamName(name: string): boolean {
  const n = name.toLowerCase();
  // Split on non-alphanumerics and match WHOLE tokens: a bare `.includes()`
  // reads `?author=` as an auth parameter and `?signal=` as a signature, and
  // redacting an ordinary query value hands back a URL that looks fine and
  // behaves differently — the same class of silent damage this module exists to
  // stop, pointed at the caller instead of the payload.
  const tokens = n.split(/[^a-z0-9]+/).filter(Boolean);
  if (tokens.some((t) => SECRET_PARAM_TOKENS.has(t))) return true;
  return SECRET_PARAM_SUBSTRINGS.some((h) => n.includes(h));
}

/**
 * Redact credential-bearing query/fragment parameters from a URL while leaving
 * the origin and path — the identifying part — byte-intact.
 *
 * Falls back to returning the input unchanged when it will not parse: a
 * half-redacted unparseable string is worse than an unmodified one, and the
 * caller's non-URL scrub still covers the surrounding text.
 */
export function scrubUrlSecrets(url: string): string {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return url;
  }
  let touched = false;

  // Userinfo credentials — `https://admin:s3cr3t@host/path` (CodeRabbit).
  // This one is a regression introduced BY the URL-preserving scrub: the blob
  // patterns used to have a chance at the password, and lifting URL spans out
  // of their reach removed it. A password in the authority component is a
  // credential by construction, never an identifier, so it is cleared outright
  // rather than pattern-matched.
  if (u.username || u.password) {
    u.username = '';
    u.password = '';
    touched = true;
  }

  for (const key of [...u.searchParams.keys()]) {
    if (isSecretParamName(key)) {
      u.searchParams.set(key, REDACTED_PARAM_VALUE);
      touched = true;
    }
  }
  // The fragment is not parsed as params by URL, but OAuth-style implicit flows
  // put tokens there, so treat a `k=v` fragment the same way.
  if (u.hash.includes('=')) {
    const frag = new URLSearchParams(u.hash.replace(/^#/, ''));
    let fragTouched = false;
    for (const key of [...frag.keys()]) {
      if (isSecretParamName(key)) {
        frag.set(key, REDACTED_PARAM_VALUE);
        fragTouched = true;
      }
    }
    if (fragTouched) {
      u.hash = `#${frag.toString()}`;
      touched = true;
    }
  }
  return touched ? u.toString() : url;
}

/**
 * Scrub `text` with `blobPatterns` WITHOUT shredding the URLs inside it.
 *
 * WHY THIS EXISTS. The extractor's own secret scrub redacts long base64/hex
 * runs, and its base64 character class contains `/` — so on
 * `https://drive.google.com/drive/folders/<id>` it matches
 * `com/drive/folders/<id>` as one 60-char "blob" and yields
 * `https://drive.google.[redacted]`. Measured, not theorised. That is the exact
 * payload ENG-9699 exists to preserve, so the naive fix reproduces the bug one
 * layer down and does it *more* convincingly, because a truncated-but-plausible
 * link looks saved.
 *
 * So: URLs are lifted out, scrubbed as URLs (credential params only), and the
 * aggressive blob patterns are applied to the remaining prose, where a long
 * base64 run really is a leaked key and not a document id.
 */
export function scrubPreservingIdentifiers(
  text: string,
  blobPatterns: readonly RegExp[],
): string {
  // Scan MATCH SPANS left-to-right rather than `indexOf`-ing a de-duplicated
  // URL list. The de-duplicated form looks equivalent and is not: with the same
  // URL pasted twice ("here it is <url> ... as I said, <url>"), one indexOf
  // finds only the FIRST occurrence, and the second falls into the trailing
  // prose slice where the blob patterns shred it — so the function's whole
  // contract, byte-preservation, held for occurrence 1 and silently failed for
  // occurrence 2. Spans have no such asymmetry.
  const spans: Array<[number, number]> = [];
  for (const m of text.matchAll(URL_RE)) {
    const trimmed = trimUrlPunctuation(m[0]);
    if (!trimmed) continue;
    const start = m.index ?? -1;
    if (start < 0) continue;
    spans.push([start, start + trimmed.length]);
  }
  if (spans.length === 0) return applyAll(text, blobPatterns);

  let out = '';
  let cursor = 0;
  for (const [start, end] of spans) {
    if (start < cursor) continue; // defensive: overlapping matches
    out += applyAll(text.slice(cursor, start), blobPatterns);
    out += scrubUrlSecrets(text.slice(start, end));
    cursor = end;
  }
  return out + applyAll(text.slice(cursor), blobPatterns);
}

function applyAll(text: string, patterns: readonly RegExp[]): string {
  let out = text;
  for (const re of patterns) out = out.replace(re, '[redacted]');
  return out;
}

/**
 * FNV-1a (64-bit), pure JS via BigInt. A name discriminator, NOT a security
 * primitive — but it is load-bearing for correctness, so it is not 32-bit.
 *
 * A 32-bit digest collides by birthday at roughly a 1-in-8,600 chance across a
 * thousand links and about 1.2% across ten thousand, and the consequence of a
 * collision here is not a cosmetic clash: two different URLs would produce the
 * same name, the second would be rejected as a `duplicate`, and the link would
 * be silently lost — which is the exact defect this module exists to prevent,
 * merely made rarer. 64 bits makes it negligible (CodeRabbit).
 */
function fnv1a64(s: string): string {
  const PRIME = 0x100000001b3n;
  const MASK = 0xffffffffffffffffn;
  let h = 0xcbf29ce484222325n;
  for (let i = 0; i < s.length; i += 1) {
    h ^= BigInt(s.charCodeAt(i));
    h = (h * PRIME) & MASK;
  }
  return h.toString(16).padStart(16, '0');
}

/** Prefix every detector-written memory name carries. */
export const IDENTIFIER_MEMORY_NAME_PREFIX = 'link:';

/**
 * A stable, collision-free, greppable memory name for one captured identifier.
 *
 * TWO JOBS, both load-bearing.
 *
 * 1. COLLISION. `agent_memories` carries an UNCONDITIONAL unique index
 *    `(agent_id, lower(name))`, and `promoteCandidates` dedupes on the name
 *    ALONE — never on content. A generic name ("Drive link") therefore means
 *    the SECOND distinct link an agent is ever given is dropped as a duplicate:
 *    ENG-9699's own defect, rebuilt inside its fix, silently. The name is
 *    derived from the URL so two different URLs cannot collide.
 * 2. PROVENANCE. `retired_at` has readers everywhere and no writer anywhere, so
 *    undoing a bad run means a hand-written SQL sweep — which is only safe if
 *    detector rows are distinguishable from agent-curated ones. `CandidateMemory`
 *    has no `source` field to carry that, so the prefix does it:
 *    `WHERE name LIKE 'link:%'` is the sweep. This metadata cannot be
 *    reconstructed after the fact, which is why it is here from the first write.
 */
export function identifierMemoryName(url: string): string {
  let host = '';
  try {
    host = new URL(url).host.replace(/^www\./, '');
  } catch {
    host = 'link';
  }
  const digest = fnv1a64(url);
  const name = `${IDENTIFIER_MEMORY_NAME_PREFIX} ${host} ${digest}`;
  // `parseCandidates` slices names to 80; stay well inside so nothing truncates
  // the digest and reintroduces collisions.
  return name.length <= 80 ? name : `${IDENTIFIER_MEMORY_NAME_PREFIX} ${digest}`;
}

/** True when `name` was written by the identifier detector. */
export function isIdentifierMemoryName(name: string): boolean {
  return name.trimStart().toLowerCase().startsWith(IDENTIFIER_MEMORY_NAME_PREFIX);
}

/**
 * The rule as the AGENT reads it, in its generated CLAUDE.md.
 *
 * Written to be spliced INTO the existing "Don't save ..." sentence rather than
 * added as a separate paragraph. A standalone paragraph would sit next to a
 * still-unqualified prohibition on "ephemeral task details", and two rules that
 * contradict resolve unpredictably — the model would be free to keep applying
 * the one that caused the bug.
 *
 * The second clause is the one that does the work: it names the *specific*
 * wrong behaviour that happened, in the words the agent itself used when it
 * diagnosed the incident. "Save identifiers" alone is advice; "a note that a
 * link is coming is not a substitute for the link" is a rule with a testable
 * failure case.
 */
export const AGENT_IDENTIFIER_RULE =
  'An identifier the work depends on is never an ephemeral detail: when a ' +
  'message gives you a URL, file, folder, document or ticket id, save the ' +
  'identifier itself, verbatim, before you reply — as its own short entry, ' +
  'not folded into prose. A note that a link is coming is not a substitute ' +
  'for the link. You can always re-derive a summary; you can never re-derive ' +
  'an identifier without asking the person for it again.';

/**
 * The rule as the EXTRACTION MODEL reads it, in the host-side prompt.
 *
 * Ordered AFTER the secrets instruction at the call site, so a credential-
 * bearing URL is still withheld: the host scrub is defence-in-depth, not the
 * primary control.
 */
export const EXTRACTOR_IDENTIFIER_RULE =
  'An identifier is ALWAYS durable, and is the one exception to "no one-off ' +
  'task details": if the conversation contains a URL, file, folder, document ' +
  'or ticket id that the work depends on, emit it as a "reference" memory with ' +
  'the identifier copied EXACTLY as it appeared — never paraphrased, never ' +
  'shortened, never described. Conversation history does not survive a restart, ' +
  'so an identifier that is only described is lost.';
